import React, { useState, useEffect, useRef } from 'react';
import { styles, animations } from './surveyStyles';

// Icon Components
// REMOVED CheckCircleIcon - no longer needed

const ExclamationCircleIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: "16px" }}>
        <circle cx="12" cy="12" r="10" fill="#EF4444" />
        <line x1="12" y1="8" x2="12" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <line x1="12" y1="16" x2="12.01" y2="16" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

// Decorative Component - UPDATED to accept size prop
// Decorative Component - UPDATED to use same image as buttons
const DecorativeOrb = ({ isLoading = false, size }) => {
    // Default sizes based on usage
    const defaultSize = isLoading ? 32 : 24;
    const actualSize = size || defaultSize;
    
    return (
        <img 
            src="https://framerusercontent.com/images/Ihj9XffRvCXjFrHQZQzHQkGVTY.png"
            alt="Lotus orb" 
            style={{
                display: "block", 
                marginBottom: size ? "0" : "16px", // No margin if size is specified
                width: `${actualSize}px`, 
                height: `${actualSize}px`,
                animation: isLoading ? "pulse 1.5s infinite ease-in-out" : "none",
            }} 
            onError={(e) => {
                e.target.style.display = "none";
                console.warn("Failed to load decorative orb image.");
            }}
        />
    );
};

// FadeWrapper Component
const FadeWrapper = ({ children, isVisible, onLoad, duration = 300 }) => {
    useEffect(() => {
        if (isVisible && onLoad) {
            const timer = setTimeout(onLoad, duration + 50);
            return () => clearTimeout(timer);
        }
    }, [isVisible, onLoad, duration]);

    const elementStyle = {
        filter: isVisible ? 'blur(0px)' : 'blur(5px)', 
        opacity: isVisible ? 1 : 0,
        transition: `filter ${duration}ms ease-out, opacity ${duration}ms ease-out`, 
    };

    return <div style={elementStyle}>{children}</div>;
};

// Text Reveal Component (Blur-in effect)
const AnimatedText = ({ text, duration = 700, onFinished, style, contentKey }) => { 
    const [isRevealed, setIsRevealed] = useState(false);
    const textRef = useRef(text);

    useEffect(() => {
        if (textRef.current !== text || !isRevealed) { 
            textRef.current = text;
            setIsRevealed(false); 
        }
        
        const timer = setTimeout(() => setIsRevealed(true), 50); 
        let finishTimer;
        if (onFinished) {
            finishTimer = setTimeout(onFinished, duration + 100); 
        }
        return () => {
            clearTimeout(timer);
            if (finishTimer) clearTimeout(finishTimer);
        };
    }, [text, duration, onFinished, contentKey]); 

    const textStyle = {
        ...style,
        display: style?.display || 'inline-block', 
        filter: isRevealed ? 'blur(0px)' : 'blur(5px)', 
        opacity: isRevealed ? 1 : 0,
        transition: `filter ${duration}ms ease-out, opacity ${duration}ms ease-out`,
    };
    return <span style={textStyle}>{text}</span>;
};

// Generic Reveal Wrapper for non-text elements
const RevealElement = ({ children, isRevealed, duration = 600, delay = 0, style: additionalStyle = {}, contentKey, onFinished }) => { 
    const [shouldBeVisible, setShouldBeVisible] = useState(false); 

    useEffect(() => {
        let revealTimer;
        let finishTimer;
        if (isRevealed) {
            revealTimer = setTimeout(() => {
                setShouldBeVisible(true);
                if (onFinished) {
                   finishTimer = setTimeout(onFinished, duration + 50);
                }
            }, delay);
        } else {
            setShouldBeVisible(false); 
        }
        return () => {
            clearTimeout(revealTimer);
            if(finishTimer) clearTimeout(finishTimer);
        }
    }, [isRevealed, delay, contentKey, duration, onFinished]); 
    
    const elementStyle = {
        ...additionalStyle,
        filter: shouldBeVisible ? 'blur(0px)' : 'blur(5px)', 
        opacity: shouldBeVisible ? 1 : 0,
        transition: `filter ${duration}ms ease-out, opacity ${duration}ms ease-out`, 
    };

    return <div style={elementStyle}>{children}</div>;
};

// Rating Chip Component
const RatingChip = ({ value, isSelected, onClick, disabled }) => {
    const [isHovered, setIsHovered] = useState(false);
    
    const chipStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '48px',
        height: '48px',
        borderRadius: '24px',
        border: isSelected ? '2px solid #713094' : '1px solid #E2E8F0',
        backgroundColor: isSelected ? '#713094' : (isHovered ? '#F8FAFC' : 'white'),
        color: isSelected ? 'white' : '#475569',
        fontSize: '16px',
        fontWeight: '600',
        fontFamily: "'SF Pro', system-ui, sans-serif",
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease-in-out',
        opacity: disabled ? 0.5 : 1,
        boxShadow: isSelected ? '0px 2px 8px rgba(113, 48, 148, 0.25)' : '0px 1px 3px rgba(0, 0, 0, 0.1)',
    };

    return (
        <button
            type="button"
            style={chipStyle}
            onClick={() => !disabled && onClick(value)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            disabled={disabled}
        >
            {value}
        </button>
    );
};

// --- Survey Content Library ---
const surveyLibrary = {
  "reflection": {
    title: "Daily Reflection",
    questions: [
        { id: "feedback1", type: "textarea", title: "What have you been using Lotus for recently?", placeholder: "Share your thoughts..." },
        { id: "feedback2", type: "textarea", title: "Did you learn something new or helpful?", placeholder: "Your suggestions are valuable..." },
        { id: "feedback3", type: "textarea", title: "Was there anything frustrating or confusing?", placeholder: "Feel free to share more..." },
    ],
  },
  "pre-use": {
    title: "Pre-Use Assessment",
    questions: [
        { id: "q1", type: "rating", title: "I feel confident finding health information that is relevant to my needs.", scale: [1, 2, 3, 4] },
        { id: "q2", type: "rating", title: "I understand complex health information from my medical records or apps.", scale: [1, 2, 3, 4] },
        { id: "q3", type: "rating", title: "I feel knowledgeable about my personal health using my current health tools.", scale: [1, 2, 3, 4] },
        { id: "q4", type: "rating", title: "I feel prepared to discuss my health with healthcare providers using my current methods.", scale: [1, 2, 3, 4] },
        { id: "q5", type: "rating", title: "I find it easy to navigate and access my personal health information using my current health apps.", scale: [1, 2, 3, 4] },
        { id: "q6", type: "rating", title: "My current apps help me organize and review health data from multiple sources (e.g., medical records, wearables).", scale: [1, 2, 3, 4] },
        { id: "q7", type: "rating", title: "I can quickly find health insights and recommendations using my current tools.", scale: [1, 2, 3, 4] },
        { id: "q8", type: "rating", title: "My current apps help me identify actionable steps to improve my health.", scale: [1, 2, 3, 4] },
        { id: "q9", type: "rating", title: "I feel confident making health-related decisions with the support of my current apps.", scale: [1, 2, 3, 4] },
        { id: "q10", type: "rating", title: "I regularly plan or adjust my health routine based on insights from my current health tools.", scale: [1, 2, 3, 4] },
        { id: "q11", type: "rating", title: "My current health apps make me feel proactive in managing my health.", scale: [1, 2, 3, 4] },
        { id: "q12", type: "rating", title: "I believe these tools help me stay on track with my health goals.", scale: [1, 2, 3, 4] },
        { id: "q13", type: "rating", title: "I would recommend my current health apps to others looking to manage their health.", scale: [1, 2, 3, 4] },
    ],
  },
  "post-use": {
    title: "Post-Use Assessment",
    questions: [
      // Direct Comparison Questions (Same as pre-use but with Lotus)
      { 
        id: "post_q1", 
        type: "rating", 
        title: "I feel confident finding health information that is relevant to my needs using Lotus.", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q2", 
        type: "rating", 
        title: "Lotus helps me understand complex health information from my medical records.", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q3", 
        type: "rating", 
        title: "I feel more knowledgeable about my health after using Lotus.", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q4", 
        type: "rating", 
        title: "I feel more prepared to discuss my health with healthcare providers after using Lotus.", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q5", 
        type: "rating", 
        title: "I find it easy to navigate and access my personal health information using Lotus.", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q6", 
        type: "rating", 
        title: "Lotus helps me organize and review health data from multiple sources (e.g., EHRs, Apple Health).", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q7", 
        type: "rating", 
        title: "I can quickly find health insights and recommendations using Lotus.", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q8", 
        type: "rating", 
        title: "Lotus helps me identify actionable steps to improve my health.", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q9", 
        type: "rating", 
        title: "I feel more confident making health-related decisions after receiving insights from Lotus.", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q10", 
        type: "rating", 
        title: "I have used Lotus to plan or adjust my health routine based on its recommendations.", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q11", 
        type: "rating", 
        title: "Using Lotus makes me feel more proactive in managing my health.", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q12", 
        type: "rating", 
        title: "I believe Lotus helps me stay on track with my health goals.", 
        scale: [1, 2, 3, 4, 5] 
      },
      { 
        id: "post_q13", 
        type: "rating", 
        title: "I would recommend Lotus to others looking to manage their health more effectively.", 
        scale: [1, 2, 3, 4, 5] 
      },
      
      // Product Feedback
      { 
        id: "post_improvements", 
        type: "textarea", 
        title: "What improvements would make the biggest difference to your experience with Lotus?", 
        placeholder: "Share your ideas for features, design changes, or anything else that would help..." 
      },
      
      // Pricing Feedback
      { 
        id: "post_pricing", 
        type: "rating", 
        title: "Based on the value you've received, what seems like a fair monthly price? (You can keep using Lotus for free - we're just curious!)", 
        scale: ["$0", "$10", "$20", "$50+"],
        labels: ["Nothing", "$10/month", "$20/month", "$50+/month"]
      },
      
      // Open Feedback
      { 
        id: "post_additional_feedback", 
        type: "textarea", 
        title: "Is there anything else you'd like to share about your experience with Lotus?", 
        placeholder: "Any other thoughts, suggestions, or feedback are welcome..." 
      },
      { 
  id: "post_continue", 
  type: "rating", 
  title: "How likely are you to continue using Lotus after this study?", 
  scale: [1, 2, 3, 4, 5] 
},
    ]
  },
  "check-d15": {
    title: "15-Day Check-in",
    questions: [
        // These are the original pre-use questions, now moved to d15
        { id: "d15_q1", type: "rating", title: "I feel confident finding health information that is relevant to my needs using Lotus.", scale: [1, 2, 3, 4] },
        { id: "d15_q2", type: "rating", title: "Lotus helps me understand complex health information from my medical records.", scale: [1, 2, 3, 4] },
        { id: "d15_q3", type: "rating", title: "I feel more knowledgeable about my health after using Lotus.", scale: [1, 2, 3, 4] },
        { id: "d15_q4", type: "rating", title: "I feel more prepared to discuss my health with healthcare providers after using Lotus.", scale: [1, 2, 3, 4] },
        { id: "d15_q5", type: "rating", title: "I find it easy to navigate and access my personal health information using Lotus.", scale: [1, 2, 3, 4] },
        { id: "d15_q6", type: "rating", title: "Lotus helps me organize and review health data from multiple sources (e.g., EHRs, Apple Health).", scale: [1, 2, 3, 4] },
        { id: "d15_q7", type: "rating", title: "I can quickly find health insights and recommendations using Lotus.", scale: [1, 2, 3, 4] },
        { id: "d15_q8", type: "rating", title: "Lotus helps me identify actionable steps to improve my health.", scale: [1, 2, 3, 4] },
        { id: "d15_q9", type: "rating", title: "I feel more confident making health-related decisions after receiving insights from Lotus.", scale: [1, 2, 3, 4] },
        { id: "d15_q10", type: "rating", title: "I have used Lotus to plan or adjust my health routine based on its recommendations.", scale: [1, 2, 3, 4] },
        { id: "d15_q11", type: "rating", title: "Using Lotus makes me feel more proactive in managing my health.", scale: [1, 2, 3, 4] },
        { id: "d15_q12", type: "rating", title: "I believe Lotus helps me stay on track with my health goals.", scale: [1, 2, 3, 4] },
        { id: "d15_q13", type: "rating", title: "I would recommend Lotus to others looking to manage their health more effectively.", scale: [1, 2, 3, 4] },
    ],
  },
  "check-d30": {
    title: "30-Day Check-in",
    questions: [
        { id: "d30_q1", type: "textarea", title: "Looking back on your month with Lotus, what stands out?", placeholder: "Reflect on your journey..." },
        { id: "d30_q2", type: "textarea", title: "How would you describe Lotus to a friend?", placeholder: "In your own words..." },
        { id: "d30_q3", type: "textarea", title: "What would you like to see added or changed?", placeholder: "Final thoughts and suggestions..." },
    ],
  },
};

// --- Main App Component ---
export default function App() {
    const [currentPhase, setCurrentPhase] = useState("loading");
    const [currentQuestionStep, setCurrentQuestionStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [isPrimaryButtonHovered, setIsPrimaryButtonHovered] = useState(false);
    const [isSecondaryButtonHovered, setIsSecondaryButtonHovered] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [isContentLoaded, setIsContentLoaded] = useState(false);
    
    const [intercomData, setIntercomData] = useState(null);
    const [surveyType, setSurveyType] = useState(null);
    const [currentSurvey, setCurrentSurvey] = useState(null);

    const { titleDuration, descriptionDuration, elementRevealDuration, elementRevealDelayIncrement } = animations;

    useEffect(() => {
        setIsMounted(true);
        
        // Debug logging for iframe detection
        const isInIframe = window.self !== window.top;
        console.log('=== REACT APP DEBUG START ===');
        console.log('App loaded. In iframe:', isInIframe);
        console.log('URL params:', window.location.search);
        console.log('User agent:', navigator.userAgent);
        console.log('Available APIs:', {
            intercom: !!window.intercom,
            INTERCOM_MESSENGER_SHEET_LIBRARY: !!window.INTERCOM_MESSENGER_SHEET_LIBRARY
        });
        
        // Send early ready signal
        if (window.parent && window.parent.postMessage && window.parent !== window) {
            console.log('Sending REACT_APP_STARTING message');
            window.parent.postMessage({
                type: 'REACT_APP_STARTING',
                timestamp: Date.now()
            }, '*');
        }
        
        // Listen for messages from parent iframe
        const messageHandler = (event) => {
            console.log('React app received message from parent:', event.data);
            if (event.data.type === 'IFRAME_READY') {
                console.log('Parent iframe is ready, initializing survey');
                
                // Send ready confirmation
                setTimeout(() => {
                    if (window.parent && window.parent.postMessage) {
                        window.parent.postMessage({
                            type: 'REACT_APP_READY',
                            timestamp: Date.now()
                        }, '*');
                    }
                }, 500);
            }
        };
        
        window.addEventListener('message', messageHandler);

        const initializeApp = (data) => {
            console.log('initializeApp called with data:', data);
            
            try {
                if (!data) {
                    // In standalone mode, create minimal data structure
                    data = {
                        contact: { id: 'standalone_user' },
                        context: {}
                    };
                    console.log('No data provided, using standalone mode');
                }
                
                setIntercomData(data);

                // Get survey type from multiple sources
                const urlParams = new URLSearchParams(window.location.search);
                const type = urlParams.get('survey') || 
                            data.context?.component_id || 
                            data.context?.surveyType || 
                            "reflection";
                
                console.log('Survey type determined:', type);
                setSurveyType(type);
                
                const surveyData = surveyLibrary[type];
                console.log('Survey data found:', !!surveyData, surveyData?.title);
                console.log('Available survey types:', Object.keys(surveyLibrary));
                
                if (surveyData) {
                    setCurrentSurvey(surveyData);
                    const initialFormData = surveyData.questions.reduce((acc, q) => ({ ...acc, [q.id]: "" }), {});
                    setFormData(initialFormData);
                    setCurrentPhase("survey");
                    
                    // Send SURVEY_LOADED message to parent immediately
                    console.log('Sending SURVEY_LOADED message to parent iframe');
                    if (window.parent && window.parent.postMessage && window.parent !== window) {
                        window.parent.postMessage({
                            type: 'SURVEY_LOADED',
                            surveyType: type,
                            timestamp: Date.now()
                        }, '*');
                    }
                } else {
                    const errorMsg = `Survey type "${type}" not found in surveyLibrary.`;
                    console.error(errorMsg);
                    console.log('Available survey types:', Object.keys(surveyLibrary));
                    setCurrentPhase("error");
                    setErrors({ form: "Survey configuration not found." });
                    
                    // Notify parent of error
                    if (window.parent && window.parent.postMessage && window.parent !== window) {
                        window.parent.postMessage({
                            type: 'SURVEY_ERROR',
                            error: errorMsg
                        }, '*');
                    }
                }
            } catch (error) {
                console.error('Error in initializeApp:', error);
                setCurrentPhase("error");
                setErrors({ form: "Initialization error occurred." });
                
                // Notify parent of error
                if (window.parent && window.parent.postMessage && window.parent !== window) {
                    window.parent.postMessage({
                        type: 'SURVEY_ERROR',
                        error: error.message
                    }, '*');
                }
            }
        };

        // Initialize based on environment
        if (window.intercom && window.intercom.render) {
            console.log('Intercom object found, calling window.intercom.render');
            try {
                window.intercom.render(initializeApp);
            } catch (error) {
                console.error('Error calling window.intercom.render:', error);
                // Fallback to standalone mode
                initializeStandaloneMode();
            }
        } else {
            console.warn("Intercom script not found. Running in standalone mode.");
            initializeStandaloneMode();
        }
        
        function initializeStandaloneMode() {
            // For standalone mode, initialize immediately
            const urlParams = new URLSearchParams(window.location.search);
            const surveyType = urlParams.get('survey') || 'reflection';
            
            // Send ready signal
            setTimeout(() => {
                if (window.parent && window.parent.postMessage) {
                    window.parent.postMessage({
                        type: 'REACT_APP_READY',
                        timestamp: Date.now()
                    }, '*');
                }
            }, 1000);
            
            // Send SURVEY_LOADED for standalone mode
            setTimeout(() => {
                if (window.parent && window.parent.postMessage && window.parent !== window) {
                    window.parent.postMessage({
                        type: 'SURVEY_LOADED',
                        surveyType: surveyType,
                        timestamp: Date.now()
                    }, '*');
                }
            }, 2000);
            
            // Initialize with dummy data
            initializeApp({
                contact: { id: 'test_contact_123', first_name: 'Test' },
                context: { component_id: surveyType }
            });
        }
        
        // Cleanup
        return () => {
            window.removeEventListener('message', messageHandler);
        };
        
        console.log('=== REACT APP DEBUG END ===');
    }, []);

    useEffect(() => {
        setIsContentLoaded(false);
    }, [currentPhase, currentQuestionStep]);

    const handleContentLoaded = () => setIsContentLoaded(true);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    };

    const handleRatingChange = (questionId, value) => {
        setFormData((prev) => ({ ...prev, [questionId]: value.toString() }));
        if (errors[questionId]) setErrors((prev) => ({ ...prev, [questionId]: null }));
    };

    const validateCurrentInput = () => {
        if (!currentSurvey || currentPhase !== "survey") return true;
        const fieldToValidate = currentSurvey.questions[currentQuestionStep];
        const value = formData[fieldToValidate.id];
        const error = (!value || !value.trim()) ? "This field is required." : null;
        setErrors(error ? { [fieldToValidate.id]: error } : {});
        return !error;
    };

    const handleNextPhase = () => {
        if (!validateCurrentInput()) return;
        if (currentQuestionStep < currentSurvey.questions.length - 1) {
            setCurrentQuestionStep((prev) => prev + 1);
        } else {
            setCurrentPhase("review");
        }
    };

    const handlePrevious = () => {
        if (currentPhase === "review") {
            setCurrentPhase("survey");
        } else if (currentQuestionStep > 0) {
            setCurrentQuestionStep((prev) => prev - 1);
        }
        setErrors({});
    };

    const handleSubmit = async () => {
    console.log('=== SUBMIT DEBUG START ===');
    console.log('handleSubmit called for survey type:', surveyType);
    console.log('Intercom data:', intercomData);
    console.log('Current survey:', currentSurvey?.title);
    console.log('Form data:', formData);
    
    if (!currentSurvey) {
        console.error('No survey data available for submission');
        return;
    }
    
    const responses = currentSurvey.questions.map((q) => ({
    id: q.id,  // Include question ID to reduce Notion payload
    question: q.title,
    answer: formData[q.id] || "",
}));

    // CRITICAL: For Intercom sheets, send EXACTLY this format
    const sheetSubmissionData = {
        surveyType: surveyType,
        surveyResponses: responses,
    };
    
    console.log("Sheet submission data:", JSON.stringify(sheetSubmissionData, null, 2));
    
    // Set submitting state
    setCurrentPhase("submitting");
    
    // Check environment
    const isInIframe = window.self !== window.top;
    const hasSheetLibrary = !!(window.INTERCOM_MESSENGER_SHEET_LIBRARY);
    const hasSubmitSheet = !!(window.INTERCOM_MESSENGER_SHEET_LIBRARY?.submitSheet);
    
    console.log('Environment check:', { 
        isInIframe,
        hasSheetLibrary,
        hasSubmitSheet,
        windowLocation: window.location.href,
        parentLocation: isInIframe ? 'iframe' : 'top',
    });
    
    // IMPORTANT: Check if we're in the Vercel iframe (nested inside Intercom sheet)
    const isNestedIframe = isInIframe && window.location.hostname === 'intercom-surveys.vercel.app';
    
    if (isNestedIframe) {
        console.log('✅ Detected nested iframe environment - posting message to parent');
        
        try {
            // Post message to parent iframe which has Intercom context
            window.parent.postMessage({
                type: 'SURVEY_COMPLETED',
                payload: sheetSubmissionData
            }, '*');
            
            console.log('✅ Posted SURVEY_COMPLETED message to parent');
            
            // Show success state briefly before Intercom closes
setCurrentPhase("success");
            
            // The parent will handle submitSheet and closing
            // No need to do anything else here
            return;
            
        } catch (e) {
            console.error('❌ Failed to post message to parent:', e);
            setCurrentPhase("error");
            setErrors({ 
                form: `Submission failed: ${e.message}` 
            });
            return;
        }
    }
    
    // FALLBACK: Direct sheet library access (shouldn't happen in production)
    if (hasSubmitSheet && !isNestedIframe) {
        console.log('⚠️ Direct sheet library access - this should not happen in production');
        
        try {
            await window.INTERCOM_MESSENGER_SHEET_LIBRARY.submitSheet(sheetSubmissionData);
            console.log('✅ Sheet submitted via direct access');
            setCurrentPhase("success");
            
            // Wait and close
            await new Promise(resolve => setTimeout(resolve, 1500));
            if (window.INTERCOM_MESSENGER_SHEET_LIBRARY?.close) {
                window.INTERCOM_MESSENGER_SHEET_LIBRARY.close();
            }
            return;
        } catch (e) {
            console.error('❌ Direct submitSheet failed:', e);
            setCurrentPhase("error");
            setErrors({ form: `Submission failed: ${e.message}` });
            return;
        }
    }
    
    // FALLBACK: Direct HTTP submission (for standalone testing ONLY)
    console.log('Using direct HTTP submission (test mode)');
    
    try {
        // Use test ID for standalone
        const contactId = 'test_contact_123';
        
        const apiSubmissionData = {
            intercomContactId: contactId,
            surveyType: surveyType,
            surveyResponses: responses,
        };
        
        const handlerUrl = 'https://aidanlotus--d25b7c1056e311f08af5f69ea79377d9.web.val.run';
        
        console.log('Submitting to:', handlerUrl);
        console.log('Contact ID being used:', contactId);
        console.log('Full payload:', JSON.stringify(apiSubmissionData, null, 2));
        
        const response = await fetch(handlerUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify(apiSubmissionData)
        });
        
        console.log('Response status:', response.status);
        const responseText = await response.text();
        console.log('Response body:', responseText);
        
        if (response.ok) {
            try {
                const result = JSON.parse(responseText);
                console.log('✅ Direct HTTP submission successful:', result);
            } catch (e) {
                console.log('✅ Submission successful (non-JSON response)');
            }
            
            setCurrentPhase("success");
            
            // In test mode, just show success
            setTimeout(() => {
                console.log('Test submission complete');
            }, 2000);
        } else {
            throw new Error(`HTTP ${response.status}: ${responseText}`);
        }
    } catch (error) {
        console.error('❌ Direct submission failed:', error);
        
        // Show error
        setCurrentPhase("error");
        setErrors({ 
            form: `Test submission failed: ${error.message}` 
        });
    }
    
    console.log('=== SUBMIT DEBUG END ===');
};

    // Add submitting phase
    if (currentPhase === "submitting") {
        return (
            <div style={styles.rootContainer}>
                <FadeWrapper isVisible={isContentLoaded} onLoad={handleContentLoaded}>
                    <div style={styles.submittingContainer}>
                        <div style={styles.spinner}></div>
                        <h2 style={styles.submittingTitle}>
                            Submitting Survey...
                        </h2>
                        <p style={styles.submittingDescription}>
                            Please wait while we save your responses.
                        </p>
                    </div>
                </FadeWrapper>
            </div>
        );
    }

    if (currentPhase === "error") {
        return (
            <div style={styles.rootContainer}>
                <div style={styles.contentWrapper}>
                    <ExclamationCircleIcon />
                    <AnimatedText 
                        text="Survey Error" 
                        style={styles.title} 
                        duration={titleDuration} 
                        contentKey="error-title"
                    />
                    <p style={styles.description}>
                        {errors.form || "Survey configuration not found."}
                    </p>
                    <p style={{...styles.description, fontSize: "14px", marginTop: "16px"}}>
                        Survey type: {surveyType || 'unknown'}<br/>
                        Available types: {Object.keys(surveyLibrary).join(', ')}
                    </p>
                </div>
            </div>
        );
    }

    // UPDATED SUCCESS PHASE - Using orb instead of checkmark
    if (currentPhase === "success") {
        return (
            <div style={styles.rootContainer}>
                <div style={{
                    ...styles.contentWrapper,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    minHeight: '70vh',
                }}>
                    <DecorativeOrb size={48} />
                    <AnimatedText 
                        text="Thank You!" 
                        style={{
                            ...styles.title,
                            marginTop: "16px",
                        }} 
                        duration={titleDuration} 
                        contentKey="success-title"
                    />
                    <p style={styles.description}>
                        Your {surveyType} survey has been submitted successfully.
                    </p>
                </div>
            </div>
        );
    }

    if (currentPhase === "loading" || !currentSurvey) {
        return (
            <div style={styles.rootContainer}>
                <div style={styles.contentWrapper}>
                    <DecorativeOrb isLoading={true} />
                    <AnimatedText 
                        text="Loading..." 
                        style={styles.description} 
                        duration={titleDuration} 
                        contentKey="loading"
                    />
                    <p style={{...styles.description, fontSize: "12px", marginTop: "8px"}}>
                        Survey type: {surveyType || 'detecting...'}
                    </p>
                </div>
            </div>
        );
    }

    let phaseContent;

    if (currentPhase === "survey") {
        const currentQ = currentSurvey.questions[currentQuestionStep];
        
        phaseContent = (
            <>
                <AnimatedText
                    text={currentQ.title}
                    style={{ ...styles.title, marginBottom: "16px" }}
                    duration={titleDuration}
                    onFinished={handleContentLoaded}
                    contentKey={`current-q-title-${currentQuestionStep}`}
                />
                
                <RevealElement 
                    isRevealed={isContentLoaded} 
                    duration={elementRevealDuration} 
                    delay={0} 
                    style={{ width: "100%", marginTop: "8px" }} 
                    contentKey={`current-q-content-${currentQuestionStep}`}
                >
                    <div>
                        {currentQ.type === "textarea" ? (
                            <>
                                <textarea
                                    id={currentQ.id}
                                    name={currentQ.id}
                                    value={formData[currentQ.id] || ''}
                                    onChange={handleChange}
                                    placeholder={currentQ.placeholder}
                                    style={{
                                        ...styles.textareaField,
                                        borderColor: errors[currentQ.id] ? "#DC2626" : "#D2B5F4",
                                    }}
                                    aria-invalid={!!errors[currentQ.id]}
                                />
                            </>
                        ) : currentQ.type === "rating" ? (
    <>
        <div style={styles.ratingContainer}>
            {currentQ.scale.map((value) => (
                <RatingChip
                    key={value}
                    value={value}
                    isSelected={formData[currentQ.id] === value.toString()}
                    onClick={(val) => handleRatingChange(currentQ.id, val)}
                    disabled={false}
                />
            ))}
        </div>
        {/* Only show labels if it's NOT the pricing question */}
        {currentQ.id !== "post_pricing" && (
            <div style={styles.ratingLabels}>
                <span>Strongly Disagree</span>
                <span>Strongly Agree</span>
            </div>
        )}
    </>
) : null}
                        
                        {errors[currentQ.id] && (
                            <p style={styles.errorText}>{errors[currentQ.id]}</p>
                        )}
                    </div>
                </RevealElement>

                <RevealElement 
                    isRevealed={isContentLoaded} 
                    duration={elementRevealDuration} 
                    delay={elementRevealDelayIncrement} 
                    style={{
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    height: "80px",
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    padding: "16px 24px",
    paddingBottom: "calc(16px + env(safe-area-inset-bottom, 20px))",
    boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
    zIndex: 100,
    display: "flex",
    alignItems: "center",
}}
                    contentKey={`current-q-buttons-${currentQuestionStep}`}
                >
                    <div style={styles.buttonWrapper}>
                        {currentQuestionStep > 0 && (
                            <div style={styles.dualButtonWrapper}>
                                <button
                                    type="button"
                                    onClick={handlePrevious}
                                    style={styles.secondaryButton(false, isSecondaryButtonHovered)}
                                    onMouseEnter={() => setIsSecondaryButtonHovered(true)}
                                    onMouseLeave={() => setIsSecondaryButtonHovered(false)}
                                >
                                    Previous
                                </button>
                            </div>
                        )}
                        <div style={currentQuestionStep > 0 ? styles.dualButtonWrapper : styles.singleButtonWrapper}>
    <button
        type="button"
        onClick={handleNextPhase}
        style={styles.primaryButton(false, isPrimaryButtonHovered, currentQuestionStep === 0)}
        onMouseEnter={() => setIsPrimaryButtonHovered(true)}
        onMouseLeave={() => setIsPrimaryButtonHovered(false)}
    >
        <img 
            src="https://framerusercontent.com/images/Ihj9XffRvCXjFrHQZQzHQkGVTY.png"
            alt=""
            style={{ width: "24px", height: "24px", flexShrink: 0 }}
        />
        {currentQuestionStep < currentSurvey.questions.length - 1 ? "Next" : "Review"}
    </button>
</div>
                    </div>
                </RevealElement>
            </>
        );
    } else if (currentPhase === "review") {
        phaseContent = (
            <>
                <AnimatedText
                    text={`Review Your ${currentSurvey.title}`}
                    style={{ ...styles.title, marginBottom: "24px" }}
                    duration={titleDuration}
                    onFinished={handleContentLoaded}
                    contentKey="review-title"
                />
                
                <RevealElement 
                    isRevealed={isContentLoaded} 
                    duration={elementRevealDuration} 
                    delay={0} 
                    style={{ width: "100%", marginBottom: "32px" }} 
                    contentKey="review-content"
                >
                    <div style={styles.reviewScrollContainer} className="review-scroll-container">
                        {currentSurvey.questions.map((q, index) => (
                            <div key={q.id} style={{ marginBottom: "24px" }}>
                                <p style={styles.reviewQuestionText}>
                                    {index + 1}. {q.title}
                                </p>
                                <p style={styles.reviewAnswerText}>
                                    {q.type === "rating" && formData[q.id] 
                                        ? `${formData[q.id]} out of ${q.scale[q.scale.length - 1]}`
                                        : formData[q.id] || "[No response]"
                                    }
                                </p>
                            </div>
                        ))}
                    </div>
                </RevealElement>

                <RevealElement 
                    isRevealed={isContentLoaded} 
                    duration={elementRevealDuration} 
                    delay={elementRevealDelayIncrement} 
                    style={{
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    height: "80px",
    backgroundColor: "rgba(255, 255, 255, 0.98)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    padding: "16px 24px",
    paddingBottom: "calc(16px + env(safe-area-inset-bottom, 20px))",
    boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
    zIndex: 100,
    display: "flex",
    alignItems: "center",
}}
                    contentKey="review-buttons"
                >
                    <div style={styles.buttonWrapper}>
                        <div style={styles.dualButtonWrapper}>
                            <button
                                type="button"
                                onClick={handlePrevious}
                                style={styles.secondaryButton(false, isSecondaryButtonHovered)}
                                onMouseEnter={() => setIsSecondaryButtonHovered(true)}
                                onMouseLeave={() => setIsSecondaryButtonHovered(false)}
                            >
                                Back
                            </button>
                        </div>
                        <div style={styles.dualButtonWrapper}>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                style={styles.primaryButton(false, isPrimaryButtonHovered)}
                                onMouseEnter={() => setIsPrimaryButtonHovered(true)}
                                onMouseLeave={() => setIsPrimaryButtonHovered(false)}
                            >
                                <img 
                                    src="https://framerusercontent.com/images/Ihj9XffRvCXjFrHQZQzHQkGVTY.png"
                                    alt=""
                                    style={{ width: "24px", height: "24px", flexShrink: 0 }}
                                />
                                Submit
                            </button>
                        </div>
                    </div>
                </RevealElement>
            </>
        );
    }

    return (
        <div style={styles.rootContainer}>
            <div style={styles.contentWrapper}>
                {phaseContent}
            </div>
        </div>
    );
}